import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { DirectUpload } from "@rails/activestorage"

const SUPPORTED_EXTENSIONS = [".pdf", ".jpg", ".jpeg", ".png", ".gif", ".heic", ".tiff", ".tif", ".docx"]

export default class extends Controller {
  static targets = ["fileInput", "form", "blobs", "dropZone", "progressArea", "progressList", "textEntries", "submitTexts", "pasteTextLink", "addMoreText", "batchIdField", "continueButton", "continueButtonText"]

  connect() {
    this.uploadInProgress = false
    this.updateContinueButtonState()
    this.observeUploadStage()
  }

  disconnect() {
    if (this.stageObserver) {
      this.stageObserver.disconnect()
    }
  }

  observeUploadStage() {
    const stage = document.getElementById("add-claims-upload-stage")
    if (!stage) return

    this.stageObserver = new MutationObserver(() => this.updateContinueButtonText())
    this.stageObserver.observe(stage, { childList: true, subtree: true })
  }

  batchIdFieldTargetConnected() {
    this.updateContinueButtonState()
  }

  pickFiles(event) {
    if (this.uploadInProgress) return
    if (event.target === this.fileInputTarget) return
    this.fileInputTarget.click()
  }

  filesPicked() {
    if (this.uploadInProgress) return
    this.uploadFiles(this.fileInputTarget.files)
  }

  dragEnter(e) {
    e.preventDefault()
    e.stopPropagation()
    this.dropZoneTarget.classList.add("border-black", "bg-support-light-200")
  }

  dragOver(e) {
    e.preventDefault()
    e.stopPropagation()
  }

  dragLeave(e) {
    e.preventDefault()
    e.stopPropagation()
    if (!this.dropZoneTarget.contains(e.relatedTarget)) {
      this.dropZoneTarget.classList.remove("border-black", "bg-support-light-200")
    }
  }

  drop(e) {
    e.preventDefault()
    e.stopPropagation()
    this.dropZoneTarget.classList.remove("border-black", "bg-support-light-200")
    if (this.uploadInProgress) return

    const files = Array.from(e.dataTransfer.files)
    const supported = []
    const rejected = []

    for (const file of files) {
      const ext = "." + file.name.split(".").pop().toLowerCase()
      if (SUPPORTED_EXTENSIONS.includes(ext)) {
        supported.push(file)
      } else {
        rejected.push(file.name)
      }
    }

    if (rejected.length > 0) {
      alert(`Unsupported file type(s): ${rejected.join(", ")}.\n\nSupported formats: PDF, JPG, PNG, GIF, HEIC, TIFF, DOCX.`)
    }

    if (supported.length > 0) {
      const dt = new DataTransfer()
      supported.forEach(f => dt.items.add(f))
      this.uploadFiles(dt.files)
    }
  }

  addTextEntry() {
    // Debounce rapid repeat clicks (e.g. bouncy-mouse double-clicks that land on
    // "or, paste in text" and then on "Add more text" after it slides into the
    // same visual slot) so one physical press never creates two textareas.
    const now = performance.now()
    if (this.lastAddTextEntryAt && now - this.lastAddTextEntryAt < 300) return
    this.lastAddTextEntryAt = now

    const wrapper = document.createElement("div")
    wrapper.className = "relative"
    wrapper.innerHTML = `
      <textarea
        placeholder="Paste or type claim text here..."
        rows="6"
        class="w-full border border-support-dark-300 rounded-lg p-3 pr-9 text-sm resize-y focus:outline-none focus:ring-1 focus:ring-black focus:border-black"></textarea>
      <div class="absolute top-2 right-2 flex items-center justify-center w-6 h-6 bg-support-dark-200/50 border border-solid border-support-dark-300/50 rounded-lg cursor-pointer hover:bg-support-dark-250 hover:border-support-dark-350 transition-colors"
           data-action="click->add-claims-upload#removeTextEntry"
           aria-label="Remove text entry">
        <svg xmlns="http://www.w3.org/2000/svg" class="w-3 h-3" viewBox="0 0 256 256" fill="currentColor">
          <path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"></path>
        </svg>
      </div>
    `
    this.textEntriesTarget.appendChild(wrapper)
    wrapper.querySelector("textarea").addEventListener("input", () => this.updateTextEntryControls())
    wrapper.querySelector("textarea").focus()
    this.updateTextEntryControls()
  }

  removeTextEntry(event) {
    event.currentTarget.closest(".relative").remove()
    this.updateTextEntryControls()
  }

  updateTextEntryControls() {
    const textareas = this.textEntriesTarget.querySelectorAll("textarea")
    const count = textareas.length
    const hasEntries = count > 0
    const hasText = Array.from(textareas).some(t => t.value.trim() !== "")
    this.submitTextsTarget.classList.toggle("hidden", !hasEntries)
    this.submitTextsTarget.disabled = !hasText || this.uploadInProgress
    this.pasteTextLinkTarget.classList.toggle("hidden", hasEntries)
    this.addMoreTextTarget.classList.toggle("hidden", !hasEntries)
    if (hasEntries) {
      this.submitTextsTarget.textContent = count === 1 ? "Upload 1 text" : `Upload ${count} texts`
    }
  }

  submitTextOnly() {
    const textAreas = this.textEntriesTarget.querySelectorAll("textarea")
    const texts = Array.from(textAreas).map(t => t.value.trim()).filter(t => t !== "")
    if (texts.length === 0 || this.uploadInProgress) return

    this.uploadInProgress = true
    // Synchronously disable the submit button so a rapid repeat click (e.g. a
    // bouncy-mouse second event that fires after the fetch has already resolved)
    // cannot trigger a second submission.
    this.submitTextsTarget.disabled = true
    this.updateContinueButtonState()
    this.submitCurrentUpload()
  }

  uploadFiles(files) {
    if (files.length === 0 || this.uploadInProgress) return

    let numUploaders = 0
    let numFinished = 0

    this.uploadInProgress = true
    this.updateContinueButtonState()
    this.blobsTarget.innerHTML = ""
    this.progressListTarget.innerHTML = ""
    this.progressAreaTarget.classList.remove("hidden")

    for (let i = 0; i < files.length; i++) {
      const file = files[i]

      const staggerDelay = i * 0.4
      const card = document.createElement("div")
      card.className = "document-file-card loading flex items-center justify-between border border-transparent rounded-lg p-3"
      card.dataset.loadingIndex = i
      card.style.animationDelay = `${staggerDelay}s, ${staggerDelay}s`
      card.innerHTML = `
        <span class="text-sm font-medium truncate">${file.name}</span>
        <div class="flex items-center gap-2">
          <div class="animate-spin h-4 w-4 border-2 border-support-dark-300 border-t-black rounded-full"></div>
          <span class="text-sm text-support-dark-400" data-progress>0%</span>
        </div>
      `
      this.progressListTarget.appendChild(card)
      const progressNode = card.querySelector("[data-progress]")

      const uploader = new Uploader(file, progressNode, this.blobsTarget)
      numUploaders++

      uploader.upload((success, error) => {
        numFinished++

        if (error) {
          card.innerHTML = `
            <span class="text-sm font-medium truncate">${file.name}</span>
            <span class="text-sm text-red-600">Upload failed</span>
          `
        } else {
          progressNode.innerText = "Done"
        }

        if (numFinished === numUploaders) {
          this.submitCurrentUpload()
        }
      })
    }
  }

  submitCurrentUpload() {
    if (!this.uploadInProgress) {
      this.uploadInProgress = true
      this.updateContinueButtonState()
    }

    const formData = new FormData(this.formTarget)
    this.textEntriesTarget.querySelectorAll("textarea").forEach(textarea => {
      const text = textarea.value.trim()
      if (text !== "") {
        formData.append("paste_texts[]", text)
      }
    })

    if (Array.from(formData.keys()).filter(key => key === "files[]" || key === "paste_texts[]").length === 0) {
      this.finishUploadCycle()
      return
    }

    fetch(this.formTarget.action, {
      method: this.formTarget.method,
      body: formData,
      headers: {
        Accept: "text/vnd.turbo-stream.html",
        "X-CSRF-Token": this.csrfToken
      }
    })
      .then(async response => {
        const html = await response.text()
        const hasStream = html.includes("<turbo-stream")
        if (hasStream) {
          Turbo.renderStreamMessage(html)
        }

        if (!response.ok && !hasStream) {
          throw new Error("Upload failed")
        }
      })
      .catch(() => {
        alert("There was an error staging these uploads. Please try again.")
      })
      .finally(() => this.finishUploadCycle())
  }

  finishUploadCycle() {
    this.uploadInProgress = false
    this.blobsTarget.innerHTML = ""
    this.progressListTarget.innerHTML = ""
    this.progressAreaTarget.classList.add("hidden")
    this.fileInputTarget.value = ""
    this.textEntriesTarget.innerHTML = ""
    this.updateTextEntryControls()
    this.updateContinueButtonState()
  }

  updateContinueButtonState() {
    if (!this.hasContinueButtonTarget) return
    this.continueButtonTarget.disabled = this.uploadInProgress || !this.hasBatchIdFieldTarget || this.batchIdFieldTargets.every(field => field.value === "")
    this.updateContinueButtonText()
  }

  updateContinueButtonText() {
    if (!this.hasContinueButtonTextTarget) return
    const stage = document.getElementById("add-claims-upload-stage")
    const count = stage ? stage.querySelectorAll(".document-file-card").length : 0
    if (count > 0) {
      this.continueButtonTextTarget.textContent = `Continue with ${count} ${count === 1 ? 'file' : 'files'}`
    } else {
      this.continueButtonTextTarget.textContent = "Continue"
    }
  }

  get csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }
}

class Uploader {
  constructor(file, progressNode, blobsNode) {
    this.file = file
    this.progressNode = progressNode
    this.blobsNode = blobsNode
    this.directUpload = new DirectUpload(this.file, "/rails/active_storage/direct_uploads", this)
  }

  upload(callback) {
    this.directUpload.create((error, blob) => {
      if (error) {
        callback(false, error)
      } else {
        const hiddenField = document.createElement("input")
        hiddenField.setAttribute("type", "hidden")
        hiddenField.setAttribute("value", blob.signed_id)
        hiddenField.name = "files[]"
        this.blobsNode.appendChild(hiddenField)

        const filenameField = document.createElement("input")
        filenameField.setAttribute("type", "hidden")
        filenameField.setAttribute("value", this.file.name)
        filenameField.name = "filenames[]"
        this.blobsNode.appendChild(filenameField)

        callback(true)
      }
    })
  }

  directUploadWillStoreFileWithXHR(request) {
    request.upload.addEventListener("progress", event => this.directUploadDidProgress(event))
  }

  directUploadDidProgress(event) {
    const fractionDone = event.loaded / event.total
    this.progressNode.innerText = Math.round(fractionDone * 100) + "%"
  }
}
